
import { useState, useEffect, useContext } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as PlusSquareFill } from "bootstrap-icons/icons/plus-square-fill.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as PlusCircleFill } from "bootstrap-icons/icons/plus-circle-fill.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../api/ApiServices";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../components/utils/CustomStyles";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";
import { handleError } from "../../components/utils/AuthenticationCheck";
import AxiosApiCall from "../../api/AxiosApiCall";
import { AuthContext } from "../../AuthProvider/AuthProvider";
import formatDate from "../../components/utils/DateFormatter";


const ReturnOrder = () => {
  const [returnOrders, setReturnOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const { permissions } = useContext(AuthContext);
  // console.log("permission", permissions);


  const navigate = useNavigate();

  //axios api encryption call
  // const axios = AxiosApiCall();



  useEffect(() => {
    const fetchReturnOrder = async () => {
      setLoading(true);
      try {
        const authData = JSON.parse(localStorage.getItem("auth"));
        const token = authData?.token;
        if (!token) {
          throw new Error("No access token found.");
        }
        const response = await axios.get(ApiServices.getReturnedOrdersByCustomer, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        });

        const { status, message, data } = response.data;
        // console.log("this is all cancel order list", data);

        if (status === 200) {
          setReturnOrders(data.slice().reverse());
          toast.success(message, { toastId: "product-fetch-success" });

        } else {
          handleError({ response: { status, data: { message } } }, navigate);
        }
      } catch (error) {
        handleError(error, navigate);
      } finally {
        setLoading(false);
      }
    };

    fetchReturnOrder();
  }, []);

  // Handle Delete Confirmation
  const handleDeleteConfirmation = (productId) => {
    setConfirmationLoading(true);
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      setConfirmationLoading(false);
      if (result.isConfirmed) {
        handleDeleteProduct(productId);
      }
    });
  };

  // Handle Product Deletion
  const handleDeleteProduct = async (productId) => {
    const formData = {
      product_id: productId,
    };
    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) {
        throw new Error("No access token found.");
      }
      const response = await axios.delete(ApiServices.removereturnOrders, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: formData,
        withCredentials: true,
      });

      const updatedProduct = returnOrders.filter(
        (product) => product.product_id !== productId
      );
      setReturnOrders(updatedProduct);

      const { status, message } = response.data;
      if (status === 200) {
        toast.success(message);
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    } finally {
      setLoading(false);
    }
  };


  const handleFilter = (e) => {
    setFilterText(e.target.value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  // Define Expandable Component for Actions
  const ExpandableComponent = ({ data }) => (
    <div className="flex justify-start p-4 relative">
      {permissions.some(permission => permission.name === 'order.view') && (
        <div className="mx-2 my-2 group relative">
          <Link
            to="/admin/customer/order/return/details"
            state={{ returnOrders: data }}
          >
            <Eye className="w-5 h-5 text-darkGreen3 " />
          </Link>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block  text-darkGreen3 text-xs px-2 py-1 rounded">
            Details
          </div>
        </div>
      )}
      {permissions.some(permission => permission.name === 'order.return') && (
        <div className="mx-2 my-2 group relative">
          <Link
            to="/admin/customer/order/return/update"
            state={{ returnOrders: data }}
          >
            <PencilSquare className="w-5 h-5 text-dash4 " />
          </Link>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block text-dash4 text-xs px-2 py-1 rounded">
            Edit
          </div>
        </div>
      )}
    </div>
  );


  const columns = [
    {
      name: "Ordered Item",
      selector: (row) => {
        // Ensure row has order_items and products before accessing
        if (row.order && row.order.order_items && row.order.order_items.length > 0) {
          // Map through order_items and extract product names
          return row.order.order_items
            .map(item => item.product?.name?.toUpperCase() || "")  // Safely access product name
            .join(", ");  // Join the product names with a comma separator
        }
        return "";  // Return empty string if no items or product name
      },
      sortable: true,
      width: "400px",
    },

    {
      name: "Tracking Id",
      selector: (row) => row.order.tracking_id ? row.order.tracking_id : "NOT AVIALABLE",
      sortable: true,
      width: "200px",
    },
    {
      name: "Amount",
      selector: (row) => CurrencyFormatter(row.order.total_amount),
      sortable: true,
      width: "100px",
    },

    {
      name: "Payment Method",
      selector: (row) => row.order.payment_method.toUpperCase(),
      sortable: true,
      width: "200px",
    },
    // {
    //   name: "Rejected By",
    //   sortable: true,
    //   width: "300px",
    //   cell: (row) => {
    //     // const customerName = `${row.user?.firstname + row.user?.lastname}`
    //     const customerName = [row.user?.firstname, row.user?.lastname].filter(Boolean).join(" ");
    //     const name = row.admin?.fullname || customerName || "Not Available";
    //     const email = row.admin?.email || row.user?.email || "Not Available";

    //     return (
    //       <div>
    //         <span className="block font-bold">
    //           {name.toUpperCase()}
    //         </span>
    //         <span className="px-3 py-0.5 rounded-md text-xs bg-orange1 text-lGrey2">
    //           {email}
    //         </span>
    //       </div>
    //     );
    //   },
    // },
    // {
    //   name: "Rejected By",
    //   sortable: true,
    //   width: "300px",
    //   cell: (row) => {
    //     const customerName = [row.user?.firstname, row.user?.lastname].filter(Boolean).join(" ");
    //     const isAdmin = !!row.admin;
    //     const name = isAdmin ? row.admin.fullname : customerName || "Not Available";
    //     const email = isAdmin ? row.admin.email : row.user?.email || "Not Available";
    //     const roleLabel = isAdmin ? "Admin" : "Customer";

    //     const backgroundColor = isAdmin ? "#1D9E34" : "#4373fc"; // ✅ Fixes the logic

    //     return (
    //       <div className="py-1">
    //         <span className="block font-bold">{name.toUpperCase()}</span>
    //         <span className="block text-sm text-gray-600 mb-1">{email}</span>
    //         <span
    //           className="px-3 py-0.5 rounded-md text-xs"
    //           style={{
    //             backgroundColor: backgroundColor,
    //             color: "white",
    //             padding: "2px 5px",
    //             borderRadius: "5px",
    //             display: "inline-block",
    //           }}
    //         >
    //           {roleLabel}
    //         </span>
    //       </div>
    //     );
    //   },
    // },
    {
      name: "Rejected By",
      selector: (row) => row.admin?.fullname.toUpperCase() || "NOT AVAILABLE",
      sortable: true,
      width: "200px",

    },

    // {
    //   name: "Email",
    //   sortable: true,
    //   width: "300px",
    //   cell: (row) => {
    //     const isAdmin = !!row.admin;
    //     const email = isAdmin ? row.admin.email : row.user?.email || "Not Available";
    //     return (
    //       <div>
    //         {email}
    //       </div>
    //     );
    //   },
    // },
    {
      name: "Ordered date",
      selector: (row) => formatDate(row.created_at),
      sortable: true,
      width: "200px",
    },

  ];

  // const filteredItems = returnOrders.filter((item) =>
  //   Object.keys(item).some((key) =>
  //     item[key]?.toString().toLowerCase().includes(filterText.toLowerCase())
  //   )
  // );


  const filteredItems = returnOrders.filter((item) => {
    const search = filterText.toLowerCase();

    const searchable = [
      item.user?.firstname,
      item.user?.lastname,
      item.user?.email,
      item.admin?.fullname,
      item.admin?.email,
      item.order?.payment_method,
      item.order?.tracking_id,
      item.order?.order_items?.map(i => i.product?.name).join(" "),
    ].join(" ").toLowerCase();

    return searchable.includes(search);
  });


  const data = filteredItems.map((returnOrders) => ({
    order: returnOrders.order ? returnOrders.order : "NOT AVAILABLE",
    tracking_id: returnOrders.order.tracking_id ? returnOrders.order.tracking_id : "NOT AVAILABLE",
    total_amount: returnOrders.total_amount ? returnOrders.total_amount : "NOT AVAILABLE",
    payment_method: returnOrders.payment_method ? returnOrders.payment_method : "NOT AVAILABLE",
    user: returnOrders.user ? returnOrders.user : "NOT AVAILABLE",
    created_at: returnOrders.created_at,

  }));
  // console.log("returned ordre", data);

  // Sort data based on creation date
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  return (
    <div>
      <MainLayouts>
        <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
          <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
            <nav className="bg-gray-200 py-0 px-4 rounded-lg">
              <ol className="list-none p-0 inline-flex mb-2">
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Rejected Items
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-orange1">List</p>
                </li>
              </ol>
            </nav>
          </section>
          <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap justify-between">
            <div className="relative w-full sm:w-1/2 mb-4 sm:mb-0">
              <input
                type="search"
                className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Search"
                value={filterText}
                onChange={handleFilter}
              />
            </div>
            {/* <Link
              to="/admin/addproduct"
              className="w-full sm:w-auto text-dGrey3 bg-orange1 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
            >
              <span className="flex justify-center sm:justify-start mx-2">
                <PlusSquareFill className="mr-2" />
                Create Product
              </span>
            </Link> */}
          </div>
          {loading ? (
            <Spinner />
          ) : (
            <DataTable
              columns={columns}
              data={filteredItems}
              pagination
              highlightOnHover
              striped
              customStyles={CustomStyles}
              noDataComponent={<div>No Product found</div>}
              expandableRows
              expandableRowsComponent={ExpandableComponent}
            />
          )}
        </div>
      </MainLayouts>
    </div>
  );
};

export default ReturnOrder;

